import { useEffect, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Calendar, Download, FileText, Printer } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { formatDate, formatCurrency } from '@/utils/dateFormat';
import { useCurrency } from '@/contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/vtgmvoj5_Logo%201.jpg";
const COMPANY_NAME = "Wilyrash Farm";

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const Reports = () => {
  const [productionData, setProductionData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState('7');
  const { currencySymbol } = useCurrency();

  useEffect(() => {
    fetchReports();
  }, [days]);

  const fetchReports = async () => {
    try {
      const [statsRes, productionRes, salesRes] = await Promise.all([
        axios.get(`${API}/reports/dashboard`, getAuthHeader()),
        axios.get(`${API}/reports/production?days=${days}`, getAuthHeader()),
        axios.get(`${API}/reports/sales?days=${days}`, getAuthHeader())
      ]);

      setStats(statsRes.data);
      setProductionData(productionRes.data.reverse());
      setSalesData(salesRes.data.reverse());
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    }
  };

  // Export to CSV
  const exportToCSV = (data, filename, type) => {
    let csvContent = '';
    
    if (type === 'production') {
      csvContent = 'Date,Eggs Collected,Broken Eggs,Good Eggs,Notes\n';
      data.forEach(entry => {
        const goodEggs = (entry.eggs_collected || 0) - (entry.broken_eggs || 0);
        csvContent += `${entry.date},${entry.eggs_collected || 0},${entry.broken_eggs || 0},${goodEggs},"${entry.notes || ''}"\n`;
      });
    } else if (type === 'sales') {
      csvContent = 'Date,Customer,Quantity,Unit Price,Total,Payment Status\n';
      data.forEach(sale => {
        csvContent += `${sale.date},"${sale.customer_name || ''}",${sale.quantity || 0},${sale.unit_price || 0},${sale.total_amount || 0},${sale.payment_status || ''}\n`;
      });
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Print report as PDF
  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank');
    const reportDate = new Date().toLocaleDateString('en-GB', { timeZone: 'Africa/Lagos' });
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${COMPANY_NAME} - Report</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #333; }
            .report-container { max-width: 900px; margin: 0 auto; }
            .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #10b981; }
            .company-info { display: flex; align-items: center; gap: 15px; }
            .company-logo { width: 60px; height: 60px; border-radius: 50%; object-fit: cover; }
            .company-name { font-size: 20px; font-weight: bold; color: #10b981; }
            .company-tagline { font-size: 11px; color: #666; }
            .report-title { text-align: right; }
            .report-title h1 { font-size: 22px; color: #333; }
            .report-date { font-size: 12px; color: #666; }
            .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
            .stat-card { background: #f8fafc; padding: 15px; border-radius: 8px; text-align: center; }
            .stat-label { font-size: 10px; color: #666; text-transform: uppercase; }
            .stat-value { font-size: 20px; font-weight: bold; color: #333; }
            .section { margin-bottom: 30px; }
            .section h3 { font-size: 16px; margin-bottom: 15px; padding-bottom: 8px; border-bottom: 1px solid #e2e8f0; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { background: #10b981; color: white; padding: 10px; text-align: left; }
            th:last-child, td:last-child { text-align: right; }
            td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
            tr:nth-child(even) { background: #f8fafc; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 11px; color: #666; }
            @media print { body { padding: 15px; } }
          </style>
        </head>
        <body>
          <div class="report-container">
            <div class="header">
              <div class="company-info">
                <img src="${COMPANY_LOGO}" alt="Logo" class="company-logo" />
                <div>
                  <div class="company-name">${COMPANY_NAME}</div>
                  <div class="company-tagline">Poultry Farm Management</div>
                </div>
              </div>
              <div class="report-title">
                <h1>Operations Report</h1>
                <div class="report-date">Last ${days} Days • Generated: ${reportDate}</div>
              </div>
            </div>
            
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-label">Total Production</div>
                <div class="stat-value">${stats?.total_production || 0}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Sales</div>
                <div class="stat-value">${currencySymbol}${formatCurrency(stats?.total_sales_amount || 0)}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Eggs in Stock</div>
                <div class="stat-value">${stats?.eggs_in_stock || 0}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Pending Orders</div>
                <div class="stat-value">${stats?.pending_orders || 0}</div>
              </div>
            </div>
            
            <div class="section">
              <h3>Production Details</h3>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Eggs Collected</th>
                    <th>Broken Eggs</th>
                    <th>Good Eggs</th>
                  </tr>
                </thead>
                <tbody>
                  ${productionData.map(entry => `
                    <tr>
                      <td>${entry.date}</td>
                      <td>${entry.eggs_collected || 0}</td>
                      <td>${entry.broken_eggs || 0}</td>
                      <td>${(entry.eggs_collected || 0) - (entry.broken_eggs || 0)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="section">
              <h3>Sales Details</h3>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Quantity</th>
                    <th>Total</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${salesData.map(sale => `
                    <tr>
                      <td>${sale.date}</td>
                      <td>${sale.customer_name || '-'}</td>
                      <td>${sale.quantity || 0}</td>
                      <td>${currencySymbol}${formatCurrency(sale.total_amount || 0)}</td>
                      <td>${sale.payment_status || '-'}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="footer">
              <p>Generated by ${COMPANY_NAME} Management System</p>
              <p>This is a computer-generated report. No signature required.</p>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const COLORS = ['#059669', '#F59E0B', '#3B82F6', '#EF4444'];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Reports
          </h1>
          <p className="text-lg text-slate-600 mt-2">View detailed analytics and reports</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-500" />
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32" data-testid="days-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="export-production-csv"
              onClick={() => exportToCSV(productionData, 'production_report', 'production')}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Production CSV
            </Button>
            <Button
              data-testid="export-sales-csv"
              onClick={() => exportToCSV(salesData, 'sales_report', 'sales')}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Sales CSV
            </Button>
            <Button
              data-testid="print-report"
              onClick={handlePrintReport}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Production</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{stats?.total_production || 0}</p>
          <p className="text-sm text-slate-500 mt-1">eggs collected</p>
        </div>
        <div className="card p-6">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Sales</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{currencySymbol}{formatCurrency(stats?.total_sales_amount || 0)}</p>
          <p className="text-sm text-slate-500 mt-1">{stats?.total_eggs_sold || 0} eggs sold</p>
        </div>
        <div className="card p-6">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Eggs in Stock</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{stats?.eggs_in_stock || 0}</p>
          <p className="text-sm text-slate-500 mt-1">available eggs</p>
        </div>
        <div className="card p-6">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Pending Orders</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{stats?.pending_orders || 0}</p>
          <p className="text-sm text-slate-500 mt-1">orders to fulfill</p>
        </div>
      </div>

      {/* Production Chart */}
      <div className="card p-6">
        <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Production Trend
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={productionData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '12px' }} />
            <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E2E8F0',
                borderRadius: '0.5rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Line type="monotone" dataKey="eggs_collected" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B', r: 4 }} />
            <Line type="monotone" dataKey="broken_eggs" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444', r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Sales Chart */}
      <div className="card p-6">
        <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Sales Performance
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={salesData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '12px' }} />
            <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E2E8F0',
                borderRadius: '0.5rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Bar dataKey="total_amount" fill="#10B981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Production Table */}
      <div className="card">
        <div className="p-6 border-b">
          <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Production Details
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th className="text-right">Eggs Collected</th>
                <th className="text-right">Broken Eggs</th>
                <th className="text-right">Good Eggs</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {productionData.map((entry, index) => (
                <tr key={index}>
                  <td>{formatDate(entry.date)}</td>
                  <td className="text-right">{entry.eggs_collected}</td>
                  <td className="text-right">{entry.broken_eggs}</td>
                  <td className="text-right font-semibold">{entry.eggs_collected - entry.broken_eggs}</td>
                  <td className="max-w-xs truncate">{entry.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sales Table */}
      <div className="card">
        <div className="p-6 border-b">
          <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sales Details
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th className="text-right">Quantity</th>
                <th className="text-right">Unit Price</th>
                <th className="text-right">Total</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {salesData.map((sale, index) => (
                <tr key={index}>
                  <td>{formatDate(sale.date)}</td>
                  <td>{sale.customer_name}</td>
                  <td className="text-right">{sale.quantity}</td>
                  <td className="text-right">{currencySymbol}{formatCurrency(sale.unit_price)}</td>
                  <td className="text-right font-semibold">{currencySymbol}{formatCurrency(sale.total_amount)}</td>
                  <td>
                    <span className={sale.payment_status === 'paid' ? 'badge-success' : 'badge-warning'}>
                      {sale.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Reports;